import { useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import {
  GetSkipCategoryPreferencesQuery,
  GetSkipCategoryPreferencesQueryVariables,
  UpdateSkipCategoryPreferencesMutation,
  UpdateSkipCategoryPreferencesMutationVariables,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import {
  GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
  UPDATE_SKIP_CATEGORY_PREFERENCES_FOR_USER,
} from './ParentalControlsService'
import { countLeaves, countSkippedLeaves } from './preference-tree'

export type SkipCategoryPreferenceItem = NonNullable<
  NonNullable<NonNullable<GetSkipCategoryPreferencesQuery['user']>['parentalControlPreferences']>[number]
>

type UseSkipCategoryPreferencesParams = {
  lang: string
  contentId?: string
}

export const useSkipCategoryPreferences = ({ lang, contentId }: UseSkipCategoryPreferencesParams) => {
  const { data, loading, error, refetch } = useQuery<
    GetSkipCategoryPreferencesQuery,
    GetSkipCategoryPreferencesQueryVariables
  >(GET_SKIP_CATEGORY_PREFERENCES_FOR_USER, {
    variables: { language: lang, ...(contentId ? { id: contentId } : {}) },
    context: { requireAuth: true },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (error) {
      logger().error('Failed to fetch skip category preferences', { lang }, error)
    }
  }, [error, lang])

  const [executeUpdate, { loading: updating }] = useMutation<
    UpdateSkipCategoryPreferencesMutation,
    UpdateSkipCategoryPreferencesMutationVariables
  >(UPDATE_SKIP_CATEGORY_PREFERENCES_FOR_USER)
  const rawPreferences = (data?.user?.parentalControlPreferences ?? []).filter(isDefined)

  const parents = rawPreferences.filter((p) => p.parentId == null).toSorted((a, b) => (a.id ?? 0) - (b.id ?? 0))
  const children = rawPreferences.filter((p) => p.parentId != null)
  const orderedPreferences = parents.flatMap((parent) => {
    const parentChildren = children
      .filter((child) => child.parentId === parent.id)
      .toSorted((a, b) => (a.id ?? 0) - (b.id ?? 0))
    return [parent, ...parentChildren]
  })

  const queryVariables = { language: lang, ...(contentId ? { id: contentId } : {}) }

  const updateSkipPreference = async (categoryId: number, skipped: boolean) => {
    try {
      await executeUpdate({
        variables: { language: lang, updates: [{ categoryId, skipped }], ...(contentId ? { contentId } : {}) },
        context: { requireAuth: true },
        update(cache, { data: mutationData }) {
          const updatedPreferences = mutationData?.updateParentalControlPreferences
          if (!updatedPreferences) return

          const existingData = cache.readQuery<
            GetSkipCategoryPreferencesQuery,
            GetSkipCategoryPreferencesQueryVariables
          >({
            query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
            variables: queryVariables,
          })

          const existingPrefs = existingData?.user?.parentalControlPreferences ?? []
          const updatedMap = new Map(updatedPreferences.filter(isDefined).map((p) => [p.id, p]))

          const mergedPreferences = existingPrefs.map((existing) => {
            if (!existing) return existing
            const updated = updatedMap.get(existing.id)
            if (!updated) return existing
            return { ...existing, ...updated, isNew: existing.isNew }
          })

          cache.writeQuery<GetSkipCategoryPreferencesQuery, GetSkipCategoryPreferencesQueryVariables>({
            query: GET_SKIP_CATEGORY_PREFERENCES_FOR_USER,
            variables: queryVariables,
            data: {
              user: {
                __typename: 'User',
                id: data?.user?.id ?? '',
                parentalControlPreferences: mergedPreferences,
              },
            },
          })
        },
      })
    } catch (err) {
      logger().error('Failed to update skip category preference', { lang, categoryId, skipped }, err)
    }
  }

  const totalCount = countLeaves(rawPreferences)
  const totalSkippedCount = countSkippedLeaves(rawPreferences)
  const hasAppliedControls = totalSkippedCount > 0

  return {
    loading,
    updating,
    error,
    preferences: orderedPreferences,
    allPreferences: rawPreferences,
    updateSkipPreference,
    refetch,
    totalSkippedCount,
    totalCount,
    hasAppliedControls,
  }
}
